/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Form, Row, Col, notification, Spin, Radio } from 'antd';
import { Button as CrceButton } from '@crc-evans/ui-components';
import { Loader2, X } from 'lucide-react';
import '../ShippingManagement/ShipmentDashBoardPage.css';
import './PQTTestPage.css';
import PqtFormSelect from './PqtFormSelect';
import PqtFormMultiSelect from './PqtFormMultiSelect';
import { connect } from 'react-redux';
import { copyStation } from '../../redux/PQTstation/action';
import get from 'lodash/get';
import { CopyStations } from '../../redux/PQTstation/saga';
const CopyITPStationTest = ({
  isCopyModalVisible,
  setIsCopyModalVisible,
  locationData,
  onCopyStation,
  isTestCopyLoading,
  ITPPipeline,
  PQtPipeline,
  setLocation,
  selectedPipeline,
  selectStation,
  selectedProject,
  onAddSowDetails,
  isAddPipeLoading,
  isWriteAccess,
  fetchPQTStationTest,
}) => {
  const [form] = Form.useForm();
  const [sourcePipeline, setSourcePipeline] = useState(ITPPipeline);
  const [targetPipelines, setTargetPipelines] = useState(PQtPipeline);
  const [copyType, setCopyType] = useState('PQT');
  const [isCopyLoading, setIsCopyLoading] = useState(false);

  useEffect(() => {
    // Get unique ITP pipeline sections
    const uniqueITPPipelines = Array.from(
      new Set(
        ITPPipeline.filter((item) => item.pipelineSection).map((item) => item.pipelineSection)
      )
    ).map((section) => ({
      pipelineSection: `${section} (${
        ITPPipeline.find((item) => item.pipelineSection === section)?.pipeStringId
      })`,
      id: ITPPipeline.find((item) => item.pipelineSection === section)?.id,
    }));

    // Get unique PQT pipeline sections
    const uniquePQTPipelines = Array.from(
      new Set(
        PQtPipeline.filter((item) => item.pipelineSection).map((item) => item.pipelineSection)
      )
    ).map((section) => ({
      pipelineSection: section,
      id: PQtPipeline.find((item) => item.pipelineSection === section)?.id,
    }));

    if (copyType === 'ITP') {
      setSourcePipeline(uniqueITPPipelines);
    } else {
      setSourcePipeline(uniquePQTPipelines);
    }
    setTargetPipelines(uniquePQTPipelines);
  }, [ITPPipeline, PQtPipeline, copyType]);

  const showModal = () => {
    setIsCopyModalVisible(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (copyType === 'ITP' && !values.fromITP) {
        notification.warning({
          message: 'Select all required fields to proceed!',
        });
        return;
      } else if (copyType === 'PQT' && !values.fromPQT) {
        notification.warning({
          message: 'Select all required fields to proceed!',
        });
        return;
      }
      const data = {
        copyTests: {
          from: copyType === 'ITP' ? values.fromITP : values.fromPQT,
          to: values.toPQT,
        },
      };
      const payload = {
        data,
        copyType,
      };
      setIsCopyLoading(true);
      const response = await CopyStations(payload);
      if (response) {
        form.resetFields();
        setCopyType('PQT');
        await fetchPQTStationTest();
        setIsCopyModalVisible(false);
      }
      setIsCopyLoading(false);
    } catch (error) {
      console.error('Validation error:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSourcePipeline(ITPPipeline);
    setTargetPipelines(PQtPipeline);
    setIsCopyModalVisible(false);
    setCopyType('PQT');
  };

  const handleSourcePipelineChange = (value) => {
    if (copyType === 'PQT') {
      // Filter out the selected pipeline from target options
      const uniquePQTPipelines = Array.from(
        new Set(
          PQtPipeline.filter((item) => item.pipelineSection && item.id !== value).map(
            (item) => item.pipelineSection
          )
        )
      ).map((section) => ({
        pipelineSection: section,
        id: PQtPipeline.find((item) => item.pipelineSection === section)?.id,
      }));
      setTargetPipelines(uniquePQTPipelines);
    }
  };

  const copySpin = <Loader2 className="pqt-inline-spin" size={22} aria-hidden />;

  const sourceOptions = (sourcePipeline || []).map((item) => ({
    value: item.id,
    label: item.pipelineSection,
  }));
  const targetOptions = (targetPipelines || []).map((item) => ({
    value: item.id,
    label: item.pipelineSection,
  }));

  return (
    <>
      {isCopyModalVisible &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="mc-dpr-modal-backdrop mc-dpr-modal-backdrop--add-test mc-dpr-modal-backdrop--pqt-station-portal"
            role="presentation"
            onClick={() => !isCopyLoading && handleCancel()}
          >
            <div
              className="mc-dpr-modal mc-dpr-modal--medium"
              role="dialog"
              aria-labelledby="pqt-copy-pipeline-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mc-dpr-modal-head">
                <h3 id="pqt-copy-pipeline-title" className="mc-dpr-modal-title">
                  Copy pipeline tests
                </h3>
                <button
                  type="button"
                  className="mc-dpr-modal-close"
                  aria-label="Close"
                  disabled={isCopyLoading}
                  onClick={handleCancel}
                >
                  <X size={20} strokeWidth={2} aria-hidden />
                </button>
              </div>
              <div className="mc-dpr-modal-body">
                {isAddPipeLoading ? (
                  <Spin indicator={copySpin} tip="Loading...">
                    <div style={{ minHeight: '200px' }} />
                  </Spin>
                ) : (
                  <Form
                    form={form}
                    name="pqt-copy-pipeline"
                    layout="vertical"
                    autoComplete="off"
                    onValuesChange={(changed) => {
                      if (changed.fromPQT !== undefined) {
                        handleSourcePipelineChange(changed.fromPQT);
                        form.setFieldsValue({ toPQT: [] });
                      }
                      if (changed.fromITP !== undefined) {
                        handleSourcePipelineChange(changed.fromITP);
                        form.setFieldsValue({ toPQT: [] });
                      }
                    }}
                  >
                    <Row gutter={[16, 8]}>
                      <Col span={24}>
                        <Form.Item label="Copy from" className="report-radio">
                          <Radio.Group
                            style={{ display: 'inline-flex', gap: 16 }}
                            onChange={(e) => {
                              setCopyType(e.target.value);
                              form.setFieldsValue({
                                fromITP: undefined,
                                fromPQT: undefined,
                                toPQT: [],
                              });
                            }}
                            value={copyType}
                          >
                            <Radio value="PQT">PQT</Radio>
                            <Radio value="ITP">ITP</Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                      {copyType === 'ITP' ? (
                        <Col lg={12} span={24}>
                          <Form.Item
                            label="From ITP pipeline"
                            name="fromITP"
                            required
                            getValueFromEvent={(v) => v}
                            rules={[{ required: true, message: 'Select source pipeline' }]}
                          >
                            <PqtFormSelect
                              placeholder="Select source pipeline…"
                              options={sourceOptions}
                              disabled={isCopyLoading}
                            />
                          </Form.Item>
                        </Col>
                      ) : (
                        <Col lg={12} span={24}>
                          <Form.Item
                            label="From PQT pipeline"
                            name="fromPQT"
                            required
                            getValueFromEvent={(v) => v}
                            rules={[{ required: true, message: 'Select source pipeline' }]}
                          >
                            <PqtFormSelect
                              placeholder="Select source pipeline…"
                              options={sourceOptions}
                              disabled={isCopyLoading}
                            />
                          </Form.Item>
                        </Col>
                      )}
                      <Col lg={12} span={24}>
                        <Form.Item
                          label="To PQT pipelines"
                          name="toPQT"
                          getValueFromEvent={(v) => v}
                          rules={[
                            {
                              validator: (_, v) =>
                                Array.isArray(v) && v.length > 0
                                  ? Promise.resolve()
                                  : Promise.reject(new Error('Select at least one target pipeline')),
                            },
                          ]}
                        >
                          <PqtFormMultiSelect
                            placeholder="Select target pipeline(s)…"
                            options={targetOptions}
                            disabled={isCopyLoading}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                )}
                <p className="mc-dpr-modal-hint-warn" style={{ marginTop: 16 }}>
                  Note: Source pipeline data will replace existing data in target pipelines.
                </p>
              </div>
              <div className="mc-dpr-modal-foot">
                <CrceButton
                  label="Cancel"
                  variant="tertiary"
                  size="medium"
                  onClick={handleCancel}
                  disabled={isCopyLoading}
                />
                <CrceButton
                  label={isCopyLoading ? 'Copying…' : 'Copy'}
                  variant="primary"
                  size="medium"
                  onClick={handleOk}
                  disabled={isCopyLoading}
                />
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

const mapStateToProps = ({ stationPQTReducer }) => {
  const isTestCopyLoading = get(stationPQTReducer, 'isTestCopying', []);
  return {
    isTestCopyLoading,
  };
};

const mapDispatchToProps = {
  onCopyStation: copyStation,
};

export default connect(mapStateToProps, mapDispatchToProps)(CopyITPStationTest);
